import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Terminal, Plug, Unplug, Send, Trash2, AlertTriangle,
  CheckCircle, XCircle, ArrowUp, ArrowDown, Info, Copy
} from 'lucide-react';
import { useWebSerial } from '../utils/useWebSerial';
import { SCANNER_COMMANDS, buildSerialCommand, toHex, fromHex } from '../utils/scannerCommands';

const QUICK_COMMANDS = [
  { label: 'Factory Reset', operand: 0x43, data: [0x00], danger: true },
  { label: 'Read FW Version', operand: 0x43, data: [0x01] },
  { label: 'Set HID Mode', operand: 0x41, data: [0x01] },
  { label: 'Set CDC Mode', operand: 0x41, data: [0x02] },
  { label: 'Manual Trigger', operand: 0x42, data: [0x00] },
  { label: 'Continuous Mode', operand: 0x42, data: [0x01] },
  { label: 'Auto Sensing', operand: 0x42, data: [0x02] },
  { label: 'Baud 9600', operand: 0x48, data: [0x03] },
  { label: 'Baud 115200', operand: 0x48, data: [0x00] },
  { label: 'Suffix CR', operand: 0x45, data: [0x0D, 0x00] },
  { label: 'Suffix TAB', operand: 0x45, data: [0x09, 0x00] },
  { label: 'Buzzer ON', operand: 0x4D, data: [0x01] },
  { label: 'Buzzer OFF', operand: 0x4D, data: [0x00] },
];

export default function SerialConsolePage() {
  const { t } = useTranslation();
  const { isConnected, isSupported, error, log, connect, disconnect, sendCommand, clearLog } = useWebSerial();

  const [operandHex, setOperandHex] = useState('41');
  const [dataHex, setDataHex] = useState('01');
  const [permanent, setPermanent] = useState(true);
  const [rawMode, setRawMode] = useState(false);
  const [rawHex, setRawHex] = useState('');
  const [baudRate, setBaudRate] = useState(9600);

  const logEndRef = useRef(null);

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [log]);

  const handleSend = () => {
    if (rawMode) {
      const bytes = fromHex(rawHex);
      if (bytes.length > 0) sendCommand(new Uint8Array(bytes));
    } else {
      const operand = parseInt(operandHex, 16);
      const data = dataHex.trim() ? fromHex(dataHex) : [];
      const packet = buildSerialCommand(operand, data, permanent);
      sendCommand(packet);
    }
  };

  const handleQuickCommand = (cmd) => {
    const packet = buildSerialCommand(cmd.operand, cmd.data, true);
    sendCommand(packet);
  };

  const copyLog = () => {
    const text = log.map((e) => `[${e.timestamp}] ${e.direction}: ${e.message}`).join('\n');
    navigator.clipboard.writeText(text);
  };

  // Build preview
  let previewHex = '';
  if (!rawMode) {
    try {
      const operand = parseInt(operandHex, 16);
      const data = dataHex.trim() ? fromHex(dataHex) : [];
      const packet = buildSerialCommand(operand, data, permanent);
      previewHex = toHex(packet);
    } catch {
      previewHex = 'Invalid input';
    }
  }

  if (!isSupported) {
    return (
      <div className="p-8 max-w-3xl mx-auto">
        <div className="card p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-[--text-primary] mb-2">{t('serial.notSupported')}</h2>
          <p className="text-gray-500">Use Google Chrome or Microsoft Edge to access Web Serial API.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[--text-primary] flex items-center gap-3">
            <Terminal className="w-7 h-7 text-purple-400" />
            {t('serial.title')}
          </h1>
          <p className="text-sm text-gray-500 mt-1">Direct USB CDC command interface — SQ400H Protocol</p>
        </div>
        <div className="flex items-center gap-3">
          {isConnected ? (
            <button onClick={disconnect} className="btn-danger flex items-center gap-2">
              <Unplug className="w-4 h-4" />
              {t('serial.disconnect')}
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <select
                value={baudRate}
                onChange={(e) => setBaudRate(parseInt(e.target.value))}
                className="select-field w-auto text-sm"
              >
                <option value="9600">9600</option>
                <option value="19200">19200</option>
                <option value="38400">38400</option>
                <option value="115200">115200</option>
              </select>
              <button onClick={() => connect(baudRate)} className="btn-primary flex items-center gap-2">
                <Plug className="w-4 h-4" />
                {t('serial.connect')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Connection status */}
      <div className={`card p-3 mb-6 flex items-center gap-3 ${isConnected ? 'border-green-500/30' : 'border-gray-700'}`}>
        <div className={`w-3 h-3 rounded-full ${isConnected ? 'bg-green-500 animate-pulse' : 'bg-gray-600'}`} />
        <span className={`text-sm font-medium ${isConnected ? 'text-green-400' : 'text-gray-500'}`}>
          {isConnected ? `Connected @ ${baudRate} baud` : 'Disconnected — Set scanner to CDC mode first'}
        </span>
        {error && <span className="text-xs text-red-400 ml-auto">{error}</span>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Command Builder */}
        <div className="lg:col-span-2 space-y-6">
          {/* Input */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-200">Command Builder</h3>
              <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rawMode}
                  onChange={(e) => setRawMode(e.target.checked)}
                  className="accent-brand-500"
                />
                {t('serial.rawHex')}
              </label>
            </div>

            {rawMode ? (
              <div>
                <label className="label">Raw HEX bytes (space-separated)</label>
                <input
                  type="text"
                  className="input-field font-mono"
                  value={rawHex}
                  onChange={(e) => setRawHex(e.target.value)}
                  placeholder="04 41 04 08 01 AE"
                />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('serial.operand')} (hex)</label>
                  <input
                    type="text"
                    className="input-field font-mono"
                    value={operandHex}
                    onChange={(e) => setOperandHex(e.target.value.replace(/[^0-9a-fA-F]/g, '').slice(0, 2))}
                    placeholder="41"
                    maxLength={2}
                  />
                </div>
                <div>
                  <label className="label">{t('serial.data')} (space-separated)</label>
                  <input
                    type="text"
                    className="input-field font-mono"
                    value={dataHex}
                    onChange={(e) => setDataHex(e.target.value)}
                    placeholder="01"
                  />
                </div>
                <div className="col-span-2 flex items-center justify-between">
                  <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={permanent}
                      onChange={(e) => setPermanent(e.target.checked)}
                      className="accent-brand-500"
                    />
                    {t('serial.permanent')} (0x08)
                  </label>
                </div>
              </div>
            )}

            {/* Preview */}
            {!rawMode && previewHex && (
              <div className="mt-4">
                <label className="label">Packet Preview</label>
                <div className="hex-display">{previewHex}</div>
                <p className="text-[10px] text-gray-600 mt-1 font-mono">
                  [Length] [Operand] [H/D=04] [Perm={permanent ? '08' : '00'}] [Data...] [Checksum]
                </p>
              </div>
            )}

            <button
              onClick={handleSend}
              disabled={!isConnected}
              className="btn-primary mt-4 flex items-center gap-2"
            >
              <Send className="w-4 h-4" />
              {t('serial.send')}
            </button>
          </div>

          {/* Log */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-200">{t('serial.log')}</h3>
              <div className="flex gap-2">
                <button onClick={copyLog} className="btn-ghost text-xs flex items-center gap-1">
                  <Copy className="w-3.5 h-3.5" /> Copy
                </button>
                <button onClick={clearLog} className="btn-ghost text-xs flex items-center gap-1 text-red-400">
                  <Trash2 className="w-3.5 h-3.5" /> {t('serial.clear')}
                </button>
              </div>
            </div>

            <div className="bg-black/40 rounded-lg border border-[#1e2736] p-4 h-80 overflow-y-auto font-mono text-xs space-y-1">
              {log.length === 0 ? (
                <p className="text-gray-600 text-center py-8">No commands sent yet</p>
              ) : (
                log.map((entry, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <span className="text-[10px] text-gray-700 shrink-0 w-20">
                      {new Date(entry.timestamp).toLocaleTimeString()}
                    </span>
                    {entry.direction === 'out' && <ArrowUp className="w-3 h-3 text-brand-400 shrink-0 mt-0.5" />}
                    {entry.direction === 'in' && <ArrowDown className="w-3 h-3 text-green-400 shrink-0 mt-0.5" />}
                    {entry.direction === 'info' && <Info className="w-3 h-3 text-gray-500 shrink-0 mt-0.5" />}
                    {entry.direction === 'error' && <XCircle className="w-3 h-3 text-red-400 shrink-0 mt-0.5" />}
                    <span className={`break-all ${
                      entry.direction === 'out' ? 'text-brand-300' :
                      entry.direction === 'in' ? 'text-green-300' :
                      entry.direction === 'error' ? 'text-red-400' :
                      'text-gray-500'
                    }`}>
                      {entry.message}
                    </span>
                  </div>
                ))
              )}
              <div ref={logEndRef} />
            </div>
          </div>
        </div>

        {/* Quick Commands */}
        <div className="space-y-6">
          <div className="card p-6">
            <h3 className="font-semibold text-gray-200 mb-4">Quick Commands</h3>
            <div className="space-y-2">
              {QUICK_COMMANDS.map((cmd) => (
                <button
                  key={cmd.label}
                  onClick={() => handleQuickCommand(cmd)}
                  disabled={!isConnected}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm border transition-all flex items-center justify-between group
                    ${cmd.danger
                      ? 'border-red-500/20 hover:border-red-500/40 text-red-400 hover:bg-red-500/5'
                      : 'border-[#2a3348] hover:border-gray-600 text-gray-400 hover:text-gray-200 hover:bg-white/5'
                    }
                    disabled:opacity-40 disabled:cursor-not-allowed
                  `}
                >
                  <span>{cmd.label}</span>
                  <span className="text-[10px] font-mono text-gray-600 group-hover:text-gray-500">
                    0x{cmd.operand.toString(16).toUpperCase()}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Protocol Reference */}
          <div className="card p-6">
            <h3 className="font-semibold text-gray-200 mb-3">Protocol Reference</h3>
            <div className="text-xs text-gray-500 space-y-2 font-mono">
              <p className="text-gray-400">Command Packet Format:</p>
              <div className="bg-black/30 rounded p-2 text-brand-300">
                [Len] [Op] [H/D] [Perm] [Data...] [CRC]
              </div>
              <p>H/D: 0x04 (from host)</p>
              <p>Perm: 0x08 (flash save) / 0x00 (volatile)</p>
              <p>CRC: ~(sum of all) & 0xFF</p>
              <hr className="border-gray-800 my-2" />
              <p className="text-gray-400">Barcode Prefix:</p>
              <div className="bg-black/30 rounded p-2 text-brand-300">+N+S-</div>
              <p>(0x2B 0x4E 0x2B 0x53 0x2D)</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
